// https://github.com/videojs/video.js — custom control bar countdown (time left)
// Video.js is loaded asynchronously with Brightcove; do not touch window.videojs at module load.

export const PLUGIN_NAME = 'RemainingTimeCountdown'
const COMPONENT_NAME = 'RemainingTimeCountdown'
const DEFAULT_CONTROL_BAR_INDEX = 13

let registrationComplete = false

/**
 * Format seconds as m:ss or h:mm:ss (countdown-style, no leading negative).
 * @param {number} seconds
 * @returns {string}
 */
export function formatCountdown (seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }
  const s = Math.floor(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${m}:${String(sec).padStart(2, '0')}`
}

/**
 * @param {*} [videojsIn] - Video.js used to create the player. Defaults to window.videojs.
 */
export function register (videojsIn = typeof window !== 'undefined' ? window.videojs : undefined) {
  if (
    registrationComplete
    || !videojsIn
    || typeof videojsIn.getComponent !== 'function'
  ) {
    return
  }

  const Component = videojsIn.getComponent('Component')
  const registerPlugin = videojsIn.registerPlugin || videojsIn.plugin

  /**
   * @param {Object} [options={}]
   * @param {string} [options.ariaLabel]
   * @param {number} [options.controlBarIndex]
   */
  function remainingTimeCountdown (options = {}) {
    this.ready(function onPlayerReady () {
      const player = this
      // Marker on the player root (like vjs-seek-buttons); avoid vjs-remaining-countdown here —
      // that class belongs only on the control component so toolbar CSS applies correctly.
      player.addClass('vjs-remaining-countdown-plugin')
      if (!player.controlBar.getChild(COMPONENT_NAME)) {
        player.controlBar.addChild(COMPONENT_NAME, {
          ariaLabel: options.ariaLabel,
        }, options.controlBarIndex || DEFAULT_CONTROL_BAR_INDEX)
      }
    })
  }

  class RemainingTimeCountdown extends Component {
    constructor (player, options) {
      super(player, options)
      this.onUpdateRemaining = this.onUpdateRemaining.bind(this)
      const p = this.player()
      p.on('timeupdate', this.onUpdateRemaining)
      p.on('durationchange', this.onUpdateRemaining)
      p.on('loadedmetadata', this.onUpdateRemaining)
      p.on('ended', this.onUpdateRemaining)
      this.onUpdateRemaining()
    }

    createEl () {
      const root = videojsIn.dom.createEl('div', {
        className: 'vjs-remaining-countdown vjs-control vjs-time-control',
      })
      const label = this.options_.ariaLabel
      if (label) {
        root.setAttribute('role', 'timer')
        root.setAttribute('aria-live', 'polite')
        root.setAttribute('aria-label', label)
      }
      const text = videojsIn.dom.createEl('span', {
        className: 'vjs-remaining-countdown__text',
      })
      root.appendChild(text)
      return root
    }

    onUpdateRemaining () {
      const player = this.player()
      const duration = player.duration()
      const current = player.currentTime()
      const textEl = this.el().querySelector('.vjs-remaining-countdown__text')
      if (!textEl) {
        return
      }
      if (!Number.isFinite(duration) || duration <= 0) {
        textEl.textContent = '--:--'
        return
      }
      const remaining = Math.max(0, duration - current)
      textEl.textContent = formatCountdown(remaining)
    }

    dispose () {
      const p = this.player()
      p.off('timeupdate', this.onUpdateRemaining)
      p.off('durationchange', this.onUpdateRemaining)
      p.off('loadedmetadata', this.onUpdateRemaining)
      p.off('ended', this.onUpdateRemaining)
      super.dispose()
    }
  }

  videojsIn.registerComponent(COMPONENT_NAME, RemainingTimeCountdown)
  registerPlugin(PLUGIN_NAME, remainingTimeCountdown)
  registrationComplete = true
}
